import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { articlesData } from "@/mocks/articles";

const siteUrl = import.meta.env.VITE_SITE_URL || "https://mulebuyspreadsheets.quest";

export default function ArticleDetail() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const article = articlesData.find((a) => a.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center bg-background-50 pt-20">
          <div className="text-center">
            <h1 className="font-heading text-3xl font-bold text-foreground-950 mb-4">Article Not Found</h1>
            <button
              onClick={() => navigate("/")}
              className="text-primary-600 hover:text-primary-700 cursor-pointer font-semibold"
            >
              Back to MuleBuy Spreadsheet Hub
            </button>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const articleUrl = `${siteUrl}/article/${article.slug}`;

  return (
    <>
      <Navbar />
      <main>
        <section className="relative w-full pt-24 pb-12 md:pt-32 md:pb-16 bg-foreground-950">
          <div className="absolute inset-0 z-0 opacity-10">
            <img
              src={article.imageUrl}
              alt={article.imageAlt}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="relative z-10 max-w-[960px] mx-auto px-4 md:px-6 text-center">
            <p className="text-xs font-label font-semibold uppercase tracking-wider text-background-50/50 mb-4">
              MuleBuy Spreadsheet Guide &middot; June 2026
            </p>
            <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-background-50 leading-tight mb-4">
              {article.title}
            </h1>
            <p className="text-sm md:text-base text-background-50/60 mb-6 max-w-[600px] mx-auto">
              {article.description}
            </p>
            <button
              onClick={() => navigate("/")}
              className="inline-flex items-center gap-2 text-background-50/60 hover:text-background-50 text-sm font-label font-medium cursor-pointer transition-colors border-b border-background-50/20 pb-0.5"
            >
              <i className="ri-arrow-left-line text-sm"></i>
              Back to MuleBuy Spreadsheet Hub
            </button>
          </div>
        </section>

        <article className="w-full py-12 md:py-20 bg-background-50">
          <div className="max-w-[720px] mx-auto px-4 md:px-6">
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "BlogPosting",
                  headline: article.title,
                  description: article.description,
                  author: { "@type": "Organization", name: "MuleBuy Spreadsheet Hub" },
                  publisher: { "@type": "Organization", name: "MuleBuy Spreadsheet Hub" },
                  datePublished: "2026-06-15",
                  dateModified: "2026-06-22",
                  url: articleUrl,
                  mainEntityOfPage: articleUrl,
                  keywords: ["mulebuy spreadsheet", "mulebuy spreadsheets list", "china agent spreadsheet"],
                }),
              }}
            />
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "BreadcrumbList",
                  itemListElement: [
                    { "@type": "ListItem", position: 1, name: "MuleBuy Spreadsheet Hub", item: siteUrl },
                    { "@type": "ListItem", position: 2, name: article.title, item: articleUrl },
                  ],
                }),
              }}
            />

            {article.sections.map((section, i) => {
              if (section.type === "text") {
                return (
                  <section key={i} className="mb-12">
                    {section.heading && (
                      <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                        {section.heading}
                      </h2>
                    )}
                    {section.content && (
                      <p className="text-foreground-600 leading-relaxed mb-4">{section.content}</p>
                    )}
                    <p className="text-foreground-600 leading-relaxed text-sm mt-4">
                      <button onClick={() => navigate("/")} className="text-primary-600 hover:text-primary-700 cursor-pointer font-semibold underline">
                        Visit the MuleBuy Spreadsheet Hub
                      </button>
                      {" "}for more resources, category navigation, and the latest spreadsheet updates.
                    </p>
                  </section>
                );
              }

              if (section.type === "summary") {
                return (
                  <section key={i} className="mb-12">
                    {section.heading && (
                      <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                        {section.heading}
                      </h2>
                    )}
                    <div className="bg-background-100 rounded-xl p-6 md:p-8 border border-background-200/70">
                      <ul className="space-y-3">
                        {section.items?.map((item, j) => (
                          <li key={j} className="flex items-start gap-3">
                            <i className="ri-checkbox-circle-line text-accent-500 text-lg mt-0.5"></i>
                            <span className="text-foreground-700 leading-relaxed text-sm">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </section>
                );
              }

              return null;
            })}
          </div>
        </article>

        <section className="w-full py-12 md:py-16 bg-background-100">
          <div className="max-w-[960px] mx-auto px-4 md:px-6">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 text-center mb-8">
              Explore More MuleBuy Spreadsheet Guides
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {articlesData
                .filter((a) => a.slug !== slug)
                .slice(0, 6)
                .map((a) => (
                  <button
                    key={a.id}
                    onClick={() => navigate(`/article/${a.slug}`)}
                    className="group bg-background-50 rounded-xl p-5 text-left border border-background-200/50 hover:border-background-300/80 cursor-pointer transition-all"
                  >
                    <div className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center mb-3">
                      <i className={`${a.icon} text-accent-600 text-sm`}></i>
                    </div>
                    <h3 className="font-heading text-sm font-bold text-foreground-950 mb-1.5 group-hover:text-primary-600 transition-colors">
                      {a.title}
                    </h3>
                    <p className="text-xs text-foreground-500 line-clamp-2">{a.description}</p>
                  </button>
                ))}
            </div>
          </div>
        </section>

        <section className="relative w-full py-14 md:py-20 overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20earthy%20minimalist%20abstract%20gradient%20background%20with%20soft%20organic%20textures%20cream%20and%20beige%20tones%20gentle%20light%20patterns%20editorial%20photography%20style&width=1920&height=800&seq=art-cta-generic-v4&orientation=landscape"
              alt="MuleBuy Spreadsheet CTA"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-foreground-950/80"></div>
          </div>
          <div className="relative z-10 max-w-[560px] mx-auto px-4 md:px-6 text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-3">
              Start Using MuleBuy Spreadsheets
            </h2>
            <p className="text-background-50/60 mb-6 text-sm leading-relaxed">
              Return to the main hub to browse all product categories, discover trending items, and access the complete mulebuy spreadsheet resource library.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                onClick={() => navigate("/")}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-background-50 text-foreground-950 font-label font-semibold text-sm cursor-pointer hover:bg-background-100 transition-colors whitespace-nowrap"
              >
                <i className="ri-home-4-line text-base"></i>
                Back to MuleBuy Hub
              </button>
              <button
                onClick={() => navigate("/mulebuy-spreadsheet-guide")}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-background-50/30 text-background-50 font-label font-semibold text-sm cursor-pointer hover:bg-background-50/10 transition-colors whitespace-nowrap"
              >
                <i className="ri-book-open-line text-base"></i>
                Main Shopping Guide
              </button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}